import {ValidationErrors, ValidatorFn, AbstractControl} from '@angular/forms';
import {
  ADDRESS_VALIDATION_MESSAGE,
  CO_APPLICANT_ADDRESS_LINE_1,
  PRIMARY_APPLICANT_ADDRESS_LINE_1,
  EMAIL_VALIDATION_MESSAGE,
  APPLICANT_NAME_ALLOW_SPECIAL_CHARACTER,
  PRIMARY_APPLICANT_EMAIL,
  CO_APPLICANT_EMAIL,
  EMPLOYEE_PRODUCT_EMAIL_FOR_VALIDATION,
  PRIMARY_APPLICANT_PHONE_NUMBER,
  CO_APPLICANT_PHONE_NUMBER,
  PRIMARY_APPLICANT_LAST_NAME,
  CO_APPLICANT_LAST_NAME,
  APPLICANT_ADDRESS_LINE_ALLOW_SPECIAL_CHARACTER
} from "../constants";


export class CustomValidators {
  static patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        // if control is empty return no error
        return null;
      }

      // test the value of the control against the regexp supplied
      const valid = regex.test(control.value);

      // if true, return no error (no error), else return error passed in the second parameter
      return valid ? null : error;
    };
  }

  static passwordMatchValidator(control: AbstractControl) {
    if (control != null) {
      const password: string = control.get('password')?.value; // get password from our password form control
      const confirmPassword: string = control.get('confirmPassword')?.value; // get password from our confirmPassword form control
      // compare is the password math
      if (password !== confirmPassword) {
        // if they don't match, set an error in our confirmPassword form control
        control.get('confirmPassword')?.setErrors({NoPassswordMatch: true});
      }
    }
  }

  static POBoxValidator(error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        // if control is empty return no error
        return null;
      }

      // test the value of the control against the regexp for PO Box addresses
      const invalid = (new RegExp('\\b[P|p]*(OST|ost)*\\.*\\s*[O|o|0]*(ffice|FFICE)*\\.*\\s*[B|b][O|o|0][X|x]\\b')).test(control.value);

      // if true, return error passed as parameter, else return no error
      return invalid ? error : null;
    };
  }

  static socialSecurityNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (control === null || control.value === null) return null;

    let ssn: string = control.value;

    // Remove format characters '-', '_'
    ssn = ssn.replace(/\-/gi, '');
    ssn = ssn.replace(/\_/gi, '');

    // Once actual value determined - get length
    let lengthInvalid: boolean = false;
    if (ssn.length !== 9)
      lengthInvalid = true;

    //  Regular expression to check for a String containing all the same character.
    //  Ex: 111-11-1111
    const isAllDuplicateCharacters = (new RegExp('^(.)\\1*$')).test(ssn);

    if (lengthInvalid || isAllDuplicateCharacters)
      return {'required': true};
    else
      return null;
  }

  static AddressLine1Validator(control: AbstractControl): ValidationErrors | null {

    if (control === null || control.value === null) return null;

    let isPrimary = control.parent?.value.isPrimary;
    const houseNumber = control.value.split(' ')[0].trim();
    let valid =  /\d/.test(houseNumber);
    //condition to check if the houseNumber is empty/ null
    if(control.value.length > 0 && !houseNumber) valid = false;

    let address = control.value.substring(control.value.indexOf(' ') + 1);
    if (valid && address) return null;

    let message = ADDRESS_VALIDATION_MESSAGE;

    message += isPrimary
      ? ` in ${PRIMARY_APPLICANT_ADDRESS_LINE_1}`
      : ` in ${CO_APPLICANT_ADDRESS_LINE_1}`;

    return {message};
  }

  static PhoneNumberValidation(control: AbstractControl): ValidationErrors | null {

    if (control === null || control.value === null) return null;

    let isPrimary = control.parent?.value.isPrimary;
    const pattern = new RegExp("^[1-9][0-9]*$");
    let valid =  pattern.test(control.value);
    
    if(control.value.length > 0 && !control.value) valid = false;

    if (valid) return null;

    let message = isPrimary
      ? `${PRIMARY_APPLICANT_PHONE_NUMBER}`
      : `${CO_APPLICANT_PHONE_NUMBER}`;

    return {message};
  }

  static emailValidator(control: AbstractControl): ValidationErrors | null {

    if (control === null || control.value === null) return null;
    let emailAddress: string = control.value;
    let isPrimary = control.parent?.value.isPrimary;

    var lastIndexOfEmailAtSymbol = emailAddress.lastIndexOf("@");
    var stringAfterAtSymbol = emailAddress.substring(lastIndexOfEmailAtSymbol + 1);

    let isInvalidEmail: boolean = false;

    // validate email is in anystring@anystring.anystring pattern
    const pattern = /^\S+@\S+\.\S+$/;
    if (!pattern.test(emailAddress)) {
      isInvalidEmail = true;
    }

    if(emailAddress.includes('+')){
      isInvalidEmail = true;
    }

    if (!isInvalidEmail) {
      return null;
    }

    let message = EMAIL_VALIDATION_MESSAGE;

    message += isPrimary
      ? ` in ${PRIMARY_APPLICANT_EMAIL}`
      : ` in ${CO_APPLICANT_EMAIL}`;

    return {message};
  }

  static applicantFirstNameSpecialCharacterValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.applicantSpecialCharactersValidator(control.value,"First Name");
  }

  static applicantMiddleNameSpecialCharacterValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.applicantSpecialCharactersValidator(control.value,"Middle Name");
  }

  static applicantLastNameSpecialCharacterValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.applicantSpecialCharactersValidator(control.value,"Last Name");
  }

  private static applicantSpecialCharactersValidator(fieldValue: any,fieldLabel: string): ValidationErrors | null {
    if (!fieldValue) return null;

    const pattern = /^[a-zA-Z0-9.'\s-]+$/;
    if (pattern.test(fieldValue)) {
      return null;
    }

    let message = `${fieldLabel} ${APPLICANT_NAME_ALLOW_SPECIAL_CHARACTER}`;
    return { message };
  }

  static applicantLastNameTrustValidation(control: AbstractControl): ValidationErrors | null {
    if (control === null || control.value === null) return null;

    let isPrimary = control.parent?.value.isPrimary;
    const pattern = new RegExp("^((?!trust).)*$");
    let lastName = control.value.toLowerCase();
    
    if(lastName == "trust") {
      return null;
    }

    let valid =  pattern.test(lastName);
    if(control.value.length > 0 && !control.value) valid = false;

    if (valid) return null;

    let message = isPrimary
      ? `${PRIMARY_APPLICANT_LAST_NAME}`
      : `${CO_APPLICANT_LAST_NAME}`;

    return {message};
  }

  static applicantAddressLine1SpecialCharacterValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.isSpecialCharacters(control.value,"Address Line1");
  }
  static applicantAddressLine2SpecialCharacterValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.isSpecialCharacters(control.value,"Address Line2");
  }

  static isSpecialCharacters(fieldValue: any,fieldLabel: string): ValidationErrors | null {
    if (!fieldValue) return null;
    const pattern = /^[a-zA-Z0-9.@&*();:",'\s-]+$/;
    if(pattern.test(fieldValue)) {
      return null;
    } else {
      let message = `${fieldLabel} ${APPLICANT_ADDRESS_LINE_ALLOW_SPECIAL_CHARACTER}`;
      return {message};
    }    
  }



}
